import { Box } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilValue } from "recoil";

import { MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import theme from "@/theme";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { useDeleteMonthlyCashflowResult } from "../../../hooks/useDeleteMonthlyCashflowResult";
import { EditPropertyHistoryItem } from "../../EditPropertyHistoryItem";

import { ManagementBuildingCostHistoryEditor } from "./HistoryEditor";
import { ManagementBuildingCostType, getManagementBuildingCostTypeKey } from "./types";

export const ManagementBuildingCostHistoryItem: React.FC<{
  monthlyCashflowResult: MonthlyCashflowResult;
  costType: ManagementBuildingCostType;
}> = ({ monthlyCashflowResult, costType }) => {
  const [isShowEdior, setIsShowEditor] = React.useState(false);
  const selected_property = useRecoilValue(SelectedProperty);
  const { deleteMonthlyCashflowResult } = useDeleteMonthlyCashflowResult();

  // 編集ボタンクリック時の処理
  const onClickEdit = React.useCallback(() => {
    setIsShowEditor(true);
  }, []);

  // 削除ボタンクリック時の処理
  const onClickDelete = React.useCallback(async () => {
    const costTypeKey = getManagementBuildingCostTypeKey(costType);
    const deleteFields = {
      [costTypeKey]: null,
    };

    await deleteMonthlyCashflowResult(monthlyCashflowResult.month, deleteFields);
  }, [costType, monthlyCashflowResult.month, deleteMonthlyCashflowResult]);

  const getCostByType = (costType: ManagementBuildingCostType, data: MonthlyCashflowResult) => {
    switch (costType) {
      case "固定資産税・都市計画税":
        return data.property_tax;
      case "消耗品費":
        return data.supplies_cost;
      case "設備修繕、大規模修繕費用":
        return data.large_scale_repair_fund_cost;
      case "その他一過性の支出":
        return data.misc_onetime_cost;
      default:
        return 0;
    }
  };

  if (getCostByType(costType, monthlyCashflowResult) === undefined) {
    return null;
  }

  return (
    <Box>
      {isShowEdior ? (
        <ManagementBuildingCostHistoryEditor
          monthlyCashflowResult={monthlyCashflowResult}
          costType={costType}
          onCancel={() => setIsShowEditor(false)}
          onSave={() => setIsShowEditor(false)}
          propertyBoughtAt={selected_property?.property.bought_at || ""}
        />
      ) : (
        <EditPropertyHistoryItem
          contents={[
            { label: "支払開始月", value: dayjs(monthlyCashflowResult.month).format("YYYY/MM") },
            {
              label: "金額",
              value: getCostByType(costType, monthlyCashflowResult)
                ? `${getCostByType(costType, monthlyCashflowResult).toLocaleString()}円`
                : "-円",
            },
          ]}
          onEdit={onClickEdit}
          onDelete={onClickDelete}
        />
      )}
      {isShowEdior && (
        <CustomButton type="button" tone="text" onClick={onClickDelete} style={{ color: theme.palette.semantic["status/error-text"], marginTop: 16 }}>
          この記録を削除する
        </CustomButton>
      )}
    </Box>
  );
};
